import { useEffect, useState } from "react";
import { Users, Trash2, KeyRound, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export function AdminAccounts({ currentUserId }) {
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [adding, setAdding] = useState(false);
  const [resetFor, setResetFor] = useState(null);
  const [resetPassword, setResetPassword] = useState("");

  const load = () => api.get("/admin/accounts").then((r) => setAccounts(r.data)).catch(() => toast.error("Could not load accounts"));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      await api.post("/admin/accounts", form);
      toast.success(`Admin account created for ${form.email}`);
      setForm({ name: "", email: "", password: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAdding(false);
    }
  };

  const remove = async (acc) => {
    if (!window.confirm(`Remove admin access for ${acc.email}?`)) return;
    try {
      await api.delete(`/admin/accounts/${acc.id}`);
      toast.success("Account removed");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveNewPassword = async (acc) => {
    if (!resetPassword || resetPassword.length < 6) return toast.error("New password must be at least 6 characters");
    try {
      await api.put(`/admin/accounts/${acc.id}`, { password: resetPassword });
      toast.success(`Password updated for ${acc.email}`);
      setResetFor(null);
      setResetPassword("");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-accounts-panel" className="rounded-3xl border border-gold-line bg-white p-5 sm:p-6">
      <h2 className="font-display text-2xl font-semibold text-maroon-dark flex items-center gap-2 mb-1">
        <Users className="w-5 h-5 text-gold-text" />
        Team Access
      </h2>
      <p className="text-xs text-ink-muted mb-5">Create additional admin accounts for family or staff — everyone listed can add, edit and publish products.</p>

      <ul data-testid="admin-accounts-list" className="divide-y divide-gold-line/70 mb-6">
        {accounts.map((acc) => (
          <li key={acc.id} data-testid={`admin-account-row-${acc.id}`} className="py-3 flex items-center gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gold-soft text-maroon text-sm font-bold">{(acc.name || acc.email)[0].toUpperCase()}</span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-ink">{acc.name || "Admin"} {acc.id === currentUserId ? <span className="text-[10px] uppercase tracking-wide text-gold-text font-bold ml-1">(You)</span> : null}</p>
              <p className="truncate text-xs text-ink-muted">{acc.email}</p>
            </div>
            <button type="button" data-testid={`admin-account-reset-toggle-${acc.id}`} onClick={() => { setResetFor(resetFor === acc.id ? null : acc.id); setResetPassword(""); }} title="Reset password" className="p-2 rounded-full text-ink-muted hover:text-maroon hover:bg-gold-soft/60 transition-colors duration-200">
              <KeyRound className="w-4 h-4" />
            </button>
            <button type="button" data-testid={`admin-account-delete-btn-${acc.id}`} onClick={() => remove(acc)} disabled={acc.id === currentUserId} title={acc.id === currentUserId ? "You cannot remove your own account" : "Remove access"} className="p-2 rounded-full text-ink-muted hover:text-red-600 hover:bg-red-50 transition-colors duration-200 disabled:opacity-30 disabled:cursor-not-allowed">
              <Trash2 className="w-4 h-4" />
            </button>
            {resetFor === acc.id ? null : null}
          </li>
        ))}
      </ul>

      {resetFor ? (
        <div data-testid="admin-account-reset-panel" className="mb-6 rounded-2xl border border-gold-line bg-ivory/70 p-4">
          <p className="text-xs font-semibold text-ink mb-2">Set a new password for this account (min 6 characters)</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input data-testid="admin-account-reset-input" type="password" value={resetPassword} onChange={(e) => setResetPassword(e.target.value)} placeholder="New password" className="border-gold-line bg-white" />
            <Button type="button" data-testid="admin-account-reset-btn" onClick={() => saveNewPassword(accounts.find((a) => a.id === resetFor) || {})} className="rounded-full bg-maroon hover:bg-maroon-dark text-white font-semibold px-6">
              Update Password
            </Button>
          </div>
        </div>
      ) : null}

      <form data-testid="admin-account-add-form" onSubmit={add} className="space-y-3 rounded-2xl border border-dashed border-gold-line bg-ivory/50 p-4">
        <p className="flex items-center gap-2 text-sm font-semibold text-ink"><UserPlus className="w-4 h-4 text-gold-text" /> Add New Admin</p>
        <div className="grid sm:grid-cols-3 gap-2">
          <Input data-testid="admin-account-add-name-input" value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} placeholder="Name" className="border-gold-line bg-white" />
          <Input data-testid="admin-account-add-email-input" type="email" value={form.email} onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))} placeholder="email@example.com" required className="border-gold-line bg-white" />
          <Input data-testid="admin-account-add-password-input" type="password" value={form.password} onChange={(e) => setForm((s) => ({ ...s, password: e.target.value }))} placeholder="Password (min 6)" required className="border-gold-line bg-white" />
        </div>
        <Button data-testid="admin-account-add-btn" type="submit" disabled={adding} className="rounded-full bg-maroon hover:bg-maroon-dark text-white font-semibold px-8 disabled:opacity-50">
          {adding ? "Creating..." : "Create Admin Account"}
        </Button>
      </form>
    </div>
  );
}
